import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import { useCreateCategory, useUpdateCategory } from '@/hooks/use-categories';
import { describeError } from '@/lib/api-client';
import type { Category } from '@/types/api';

/** Create or rename/re-describe a category. The slug is derived server-side and never edited. */
export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Undefined means "create". */
  category?: Category;
}) {
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setDescription(category?.description ?? '');
  }, [open, category]);

  const pending = createCategory.isPending || updateCategory.isPending;
  const trimmedName = name.trim();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmedName || pending) return;

    const onError = (error: Error) =>
      toast({
        title: category ? 'Could not update the category' : 'Could not create the category',
        description: describeError(error),
        variant: 'error',
      });

    if (category) {
      updateCategory.mutate(
        {
          categoryId: category.categoryId,
          body: { name: trimmedName, description: description.trim() },
        },
        {
          onSuccess: () => {
            toast({ title: 'Category updated', description: trimmedName, variant: 'success' });
            onOpenChange(false);
          },
          onError,
        },
      );
      return;
    }

    createCategory.mutate(
      { name: trimmedName, description: description.trim() || undefined },
      {
        onSuccess: () => {
          toast({ title: 'Category created', description: trimmedName, variant: 'success' });
          onOpenChange(false);
        },
        onError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? 'Edit category' : 'New category'}</DialogTitle>
          <DialogDescription>
            {category
              ? 'Renaming a category keeps its slug, so existing links stay attached to it.'
              : 'The slug is generated from the name and is what links are stored against.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="category-name">Name</Label>
            <Input
              id="category-name"
              value={name}
              required
              maxLength={80}
              autoComplete="off"
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="category-description">Description</Label>
            <Textarea
              id="category-description"
              value={description}
              maxLength={500}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What belongs in this category?"
            />
          </div>

          {category ? (
            <p className="text-xs text-muted-foreground">
              Slug: <span className="font-mono">{category.slug}</span>
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmedName} loading={pending}>
              {category ? 'Save changes' : 'Create category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
